import FileUpload from "../../FileUpload";

/**
 * Iter-27.63 — Onglet "Espace Premium" : tier + couleurs + hero + features toggles.
 * Sous-composant contrôlé (form/setForm passés par le parent).
 *
 * Utilisé dans le backoffice `/admin/espace-partenaires`.
 */
const TIERS = [
    { value: "signature", label: "Signature", hint: "Prestige absolu (or)" },
    { value: "premium", label: "Premium", hint: "Partenaire payant premium" },
    { value: "impact", label: "Impact", hint: "Gros volume mission terrain" },
    { value: "engaged", label: "Engagé", hint: "Partenaire standard" },
];

const FEATURE_OPTIONS = [
    { key: "news", label: "Fil d'actualités" },
    { key: "gratitude", label: "Messages de remerciement" },
    { key: "impact_stories", label: "Histoires d'impact" },
    { key: "hero_video", label: "Bannière vidéo top" },
];

export default function PremiumSpaceTab({ form, setForm }) {
    const features = form.features || {};
    const toggleFeature = (key) => {
        setForm((f) => ({
            ...f,
            features: {
                ...(f.features || {}),
                [key]: features[key] === false ? true : !features[key],
            },
        }));
    };
    return (
        <div className="space-y-6">
            {/* Tier */}
            <div>
                <label className="text-xs font-thin tracking-[0.2em] uppercase text-white/50 mb-2 block">
                    Tier de partenariat (badge visible côté ambassadeur)
                </label>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                    {TIERS.map((t) => (
                        <button
                            key={t.value}
                            type="button"
                            onClick={() => setForm({ ...form, tier: t.value })}
                            data-testid={`premium-tier-${t.value}`}
                            className={`px-3 py-3 rounded-2xl text-left border transition-all ${
                                form.tier === t.value
                                    ? "bg-shariti-yellow/15 border-shariti-yellow text-white"
                                    : "bg-white/3 border-white/10 text-white/65 hover:text-white"
                            }`}
                        >
                            <div className="text-sm font-extrabold">Partenaire {t.label}</div>
                            <div className="text-[10px] text-white/45 mt-0.5">{t.hint}</div>
                        </button>
                    ))}
                </div>
            </div>

            {/* Tagline */}
            <div>
                <label className="text-xs font-thin tracking-[0.2em] uppercase text-white/50 mb-1 block">
                    Tagline (phrase d'accroche premium)
                </label>
                <input
                    type="text"
                    value={form.tagline || ""}
                    onChange={(e) => setForm({ ...form, tagline: e.target.value })}
                    placeholder="Ex: Pour les droits humains partout"
                    maxLength={200}
                    data-testid="premium-tagline"
                    className="w-full bg-white/5 border border-white/10 rounded-2xl px-4 py-3 text-white outline-none focus:border-shariti-orange/50"
                />
            </div>

            {/* Colors */}
            <div className="grid grid-cols-2 gap-3">
                <div>
                    <label className="text-xs font-thin tracking-[0.2em] uppercase text-white/50 mb-1 block">
                        Couleur primaire
                    </label>
                    <div className="flex items-center gap-2 bg-white/5 border border-white/10 rounded-2xl px-3 py-2">
                        <input
                            type="color"
                            value={form.primary_color || "#EE722E"}
                            onChange={(e) => setForm({ ...form, primary_color: e.target.value })}
                            data-testid="premium-primary-color"
                            className="w-10 h-10 rounded-lg border-0 bg-transparent cursor-pointer"
                        />
                        <input
                            type="text"
                            value={form.primary_color || ""}
                            onChange={(e) => setForm({ ...form, primary_color: e.target.value })}
                            placeholder="#EE722E"
                            className="flex-1 bg-transparent text-white text-sm outline-none"
                        />
                    </div>
                </div>
                <div>
                    <label className="text-xs font-thin tracking-[0.2em] uppercase text-white/50 mb-1 block">
                        Couleur d'accent
                    </label>
                    <div className="flex items-center gap-2 bg-white/5 border border-white/10 rounded-2xl px-3 py-2">
                        <input
                            type="color"
                            value={form.accent_color || "#FCDB53"}
                            onChange={(e) => setForm({ ...form, accent_color: e.target.value })}
                            data-testid="premium-accent-color"
                            className="w-10 h-10 rounded-lg border-0 bg-transparent cursor-pointer"
                        />
                        <input
                            type="text"
                            value={form.accent_color || ""}
                            onChange={(e) => setForm({ ...form, accent_color: e.target.value })}
                            placeholder="#FCDB53"
                            className="flex-1 bg-transparent text-white text-sm outline-none"
                        />
                    </div>
                </div>
            </div>

            {/* Hero image */}
            <div>
                <label className="text-xs font-thin tracking-[0.2em] uppercase text-white/50 mb-1 block">
                    Image bannière (hero, full-bleed)
                </label>
                <FileUpload
                    kind="image"
                    currentUrl={form.hero_image_url}
                    onUploaded={(url) => setForm({ ...form, hero_image_url: url })}
                    testId="premium-hero-image"
                />
            </div>

            {/* Hero video URL */}
            <div>
                <label className="text-xs font-thin tracking-[0.2em] uppercase text-white/50 mb-1 block">
                    Vidéo bannière (URL YouTube / Vimeo, optionnel)
                </label>
                <input
                    type="url"
                    value={form.hero_video_url || ""}
                    onChange={(e) => setForm({ ...form, hero_video_url: e.target.value })}
                    placeholder="https://www.youtube.com/watch?v=..."
                    data-testid="premium-hero-video"
                    className="w-full bg-white/5 border border-white/10 rounded-2xl px-4 py-3 text-white outline-none focus:border-shariti-orange/50"
                />
            </div>

            {/* Feature toggles */}
            <div>
                <label className="text-xs font-thin tracking-[0.2em] uppercase text-white/50 mb-2 block">
                    Sections actives sur l'Espace partenaire
                </label>
                <p className="text-[11px] text-white/45 mb-3 leading-relaxed">
                    Désactive une section pour la masquer côté ambassadeur. Pratique pour lancer rapidement sans attendre que tout soit prêt.
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    {FEATURE_OPTIONS.map((f) => {
                        const isOn = features[f.key] !== false;
                        return (
                            <label
                                key={f.key}
                                className={`flex items-center gap-3 px-3 py-2.5 rounded-2xl cursor-pointer transition-all ${
                                    isOn
                                        ? "bg-shariti-orange/10 border border-shariti-orange/30"
                                        : "bg-white/3 border border-white/10"
                                }`}
                            >
                                <input
                                    type="checkbox"
                                    checked={isOn}
                                    onChange={() => toggleFeature(f.key)}
                                    data-testid={`premium-feature-${f.key}`}
                                    className="accent-shariti-orange"
                                />
                                <span className="text-sm font-bold text-white/85">{f.label}</span>
                                <span
                                    className={`ml-auto text-[10px] font-extrabold uppercase tracking-wider ${
                                        isOn ? "text-shariti-orange-light" : "text-white/40"
                                    }`}
                                >
                                    {isOn ? "Actif" : "Masqué"}
                                </span>
                            </label>
                        );
                    })}
                </div>
            </div>
        </div>
    );
}
